import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { brl, fmtDate } from "../lib/format";
import { ChartBar, FilePdf, VideoCamera, Package, CalendarBlank, Percent } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";

const MONTHS_PT = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];
const fmtYm = (ym) => {
  if (!ym || ym.length < 7) return ym || "";
  const [y, m] = ym.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MONTHS_PT[idx] || m}/${y}`;
};

export default function ContractorReports() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();
  const [contractors, setContractors] = useState([]);
  const [contractorId, setContractorId] = useState(searchParams.get("contractor_id") || "");
  const [dateFrom, setDateFrom] = useState(searchParams.get("date_from") || "");
  const [dateTo, setDateTo] = useState(searchParams.get("date_to") || "");
  const [liveId, setLiveId] = useState(searchParams.get("live_id") || "");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const isContractorScoped = !!user?.contractor_id;

  useEffect(() => {
    api.get("/contractors").then(({ data }) => {
      setContractors(data);
      if (!contractorId && isContractorScoped && user?.contractor_id) {
        setContractorId(user.contractor_id);
      } else if (!contractorId && data.length === 1) {
        setContractorId(data[0].id);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = useCallback(async () => {
    if (!contractorId) return;
    setLoading(true);
    try {
      const params = { };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (liveId) params.live_id = liveId;
      const { data } = await api.get(`/reports/contractors/${contractorId}`, { params });
      setReport(data);
      // sync url
      const next = { contractor_id: contractorId };
      if (dateFrom) next.date_from = dateFrom;
      if (dateTo) next.date_to = dateTo;
      if (liveId) next.live_id = liveId;
      setSearchParams(next, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar relatório");
      setReport(null);
    }
    setLoading(false);
  }, [contractorId, dateFrom, dateTo, liveId, setSearchParams]);

  useEffect(() => { if (contractorId) load(); }, [contractorId, load]);

  const downloadPDF = async () => {
    if (!contractorId) return;
    setDownloading(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (liveId) params.live_id = liveId;
      const resp = await api.get(`/reports/contractors/${contractorId}/report.pdf`, {
        params: { ...params, _: Date.now() }, responseType: "blob",
        headers: { "Cache-Control": "no-cache" },
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const safe = (report?.contractor?.name || "contratante").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 40);
      a.href = url; a.download = `contratante_${safe}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Relatório baixado");
    } catch { toast.error("Falha ao baixar PDF"); }
    finally { setDownloading(false); }
  };

  const selectedContractor = useMemo(
    () => contractors.find(c => c.id === contractorId) || null,
    [contractors, contractorId]
  );

  return (
    <div data-testid="reports-contractors-page" className="space-y-6">
      <header>
        <p className="label-brutal">Relatórios</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-black">Prestação de contas · Contratantes</h1>
        <p className="text-brand-ink2 mt-2">
          Consolidado de vendas por contratante: resumo por Live + detalhamento por produto.
        </p>
      </header>

      <div className="card-brutal p-5 bg-brand-yellow grid grid-cols-1 md:grid-cols-[1.4fr_1fr_1fr_1fr_auto] gap-3 items-end">
        <div>
          <label className="label-brutal">Contratante *</label>
          <select
            data-testid="report-contractor-select"
            className="input-brutal"
            value={contractorId}
            onChange={(e) => { setContractorId(e.target.value); setLiveId(""); }}
            disabled={isContractorScoped}
          >
            <option value="">— escolha —</option>
            {contractors.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label-brutal">Data inicial</label>
          <input data-testid="report-date-from" type="date" className="input-brutal" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div>
          <label className="label-brutal">Data final</label>
          <input data-testid="report-date-to" type="date" className="input-brutal" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <div>
          <label className="label-brutal">Live específica</label>
          <select
            data-testid="report-live-select"
            className="input-brutal"
            value={liveId}
            onChange={(e) => setLiveId(e.target.value)}
            disabled={!report?.lives?.length}
          >
            <option value="">— todas —</option>
            {(report?.lives || []).map(lv => (
              <option key={lv.id} value={lv.id}>{lv.name} · {fmtDate(lv.date)}</option>
            ))}
          </select>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="report-download-pdf"
            onClick={downloadPDF}
            disabled={!contractorId || downloading || !report}
            className="btn-pink h-[52px] whitespace-nowrap"
          >
            <FilePdf weight="bold" size={16} /> {downloading ? "Baixando…" : "Baixar PDF"}
          </button>
        </div>
      </div>

      {loading && <div className="card-brutal p-8 text-center font-bold">Carregando…</div>}
      {!loading && !contractorId && (
        <div className="card-brutal p-10 text-center">
          <ChartBar weight="bold" size={40} className="mx-auto" />
          <p className="font-heading text-xl font-black mt-3">Escolha um contratante</p>
          <p className="text-brand-ink2 text-sm">Selecione o contratante acima para gerar o relatório.</p>
        </div>
      )}
      {!loading && contractorId && report && (
        <>
          {selectedContractor && (
            <header className="card-brutal p-5 bg-brand-pink text-white">
              <p className="text-[11px] font-bold uppercase tracking-widest">Contratante</p>
              <h2 className="font-heading text-3xl font-black">{selectedContractor.name}</h2>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-2 mt-3">
                <Stat label="Total vendido" value={brl(report.totals.total)} bg="bg-white" tone="ink" />
                <Stat label="Recebido" value={brl(report.totals.paid)} bg="bg-brand-mint" tone="ink" />
                <Stat label="A receber" value={brl(report.totals.pending)} bg="bg-brand-yellow" tone="ink" />
                <Stat label="Lives" value={String(report.totals.lives_count)} bg="bg-white" tone="ink" />
                <Stat label="Pedidos" value={String(report.totals.orders_count)} bg="bg-white" tone="ink" />
                <Stat label="Produtos" value={String(report.totals.products_count)} bg="bg-white" tone="ink" />
              </div>
              {report.commission_pct > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-2">
                  <Stat label="Comissão %" value={`${report.commission_pct}%`} bg="bg-brand-purple" tone="ink" />
                  <Stat label="Comissão calculada (sobre recebido)" value={brl(report.totals.commission)} bg="bg-brand-mint" tone="ink" />
                  <Stat label="Base (recebido)" value={brl(report.totals.paid)} bg="bg-white" tone="ink" />
                </div>
              )}
            </header>
          )}

          <section>
            <h3 className="font-heading text-xl font-black flex items-center gap-2 mb-2">
              <CalendarBlank weight="bold" size={20} /> Relatório mensal
              {report.commission_pct > 0 && (
                <span className="badge-brutal !bg-brand-purple text-xs">
                  <Percent weight="bold" size={12} /> {report.commission_pct}% comissão sobre recebido
                </span>
              )}
            </h3>
            {report.monthly.length === 0 ? (
              <div className="card-brutal p-8 text-center text-brand-ink2">Nenhum pedido no período.</div>
            ) : (
              <div className="card-brutal overflow-x-auto">
                <table className="tbl w-full">
                  <thead>
                    <tr>
                      <th className="w-28">Mês</th>
                      <th className="w-20 text-right">Pedidos</th>
                      <th className="w-28 text-right">Total</th>
                      <th className="w-28 text-right">Recebido</th>
                      <th className="w-28 text-right">A receber</th>
                      <th className="w-32 text-right">Comissão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.monthly.map(m => (
                      <tr key={m.month} data-testid={`report-month-${m.month}`}>
                        <td className="font-bold">{fmtYm(m.month)}</td>
                        <td className="text-right">{m.orders_count}</td>
                        <td className="text-right font-bold">{brl(m.gross)}</td>
                        <td className="text-right">{brl(m.paid)}</td>
                        <td className="text-right">{brl(m.pending)}</td>
                        <td className="text-right font-bold">{report.commission_pct > 0 ? brl(m.commission) : "—"}</td>
                      </tr>
                    ))}
                    <tr className="font-black bg-brand-cream" data-testid="report-month-total">
                      <td>Total</td>
                      <td className="text-right">{report.totals.orders_count}</td>
                      <td className="text-right">{brl(report.totals.total)}</td>
                      <td className="text-right">{brl(report.totals.paid)}</td>
                      <td className="text-right">{brl(report.totals.pending)}</td>
                      <td className="text-right">{report.commission_pct > 0 ? brl(report.totals.commission) : "—"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h3 className="font-heading text-xl font-black flex items-center gap-2 mb-2">
              <VideoCamera weight="bold" size={20} /> Resumo por Live
            </h3>
            {report.lives.length === 0 ? (
              <div className="card-brutal p-8 text-center text-brand-ink2">Nenhuma live no período.</div>
            ) : (
              <div className="card-brutal overflow-x-auto">
                <table className="tbl w-full">
                  <thead>
                    <tr>
                      <th className="w-28">Data</th>
                      <th>Live</th>
                      <th className="w-20 text-right">Pedidos</th>
                      <th className="w-28 text-right">Total</th>
                      <th className="w-28 text-right">Recebido</th>
                      <th className="w-28 text-right">A receber</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.lives.map(lv => (
                      <tr key={lv.id} data-testid={`report-live-row-${lv.id}`}>
                        <td>{fmtDate(lv.date)}</td>
                        <td className="font-bold">{lv.name}</td>
                        <td className="text-right">{lv.orders_count}</td>
                        <td className="text-right font-bold">{brl(lv.total)}</td>
                        <td className="text-right">{brl(lv.paid)}</td>
                        <td className="text-right">{brl(lv.pending)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h3 className="font-heading text-xl font-black flex items-center gap-2 mb-2">
              <Package weight="bold" size={20} /> Detalhamento por Produto
            </h3>
            {report.products.length === 0 ? (
              <div className="card-brutal p-8 text-center text-brand-ink2">Nenhuma venda registrada.</div>
            ) : (
              <div className="card-brutal overflow-x-auto">
                <table className="tbl w-full">
                  <thead>
                    <tr>
                      <th className="w-20">Código</th>
                      <th>Descrição</th>
                      <th className="w-16 text-right">Qtd</th>
                      <th className="w-20 text-right">Pedidos</th>
                      <th className="w-28 text-right">Total</th>
                      <th className="w-28 text-right">Recebido</th>
                      <th className="w-28 text-right">A receber</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.products.map((p, i) => (
                      <tr key={`${p.product_code}-${i}`} data-testid={`report-prod-row-${i}`}>
                        <td className="font-bold">{p.product_code || "—"}</td>
                        <td>{p.description || "—"}</td>
                        <td className="text-right">{p.quantity}</td>
                        <td className="text-right">{p.orders_count}</td>
                        <td className="text-right font-bold">{brl(p.gross)}</td>
                        <td className="text-right">{brl(p.paid)}</td>
                        <td className="text-right">{brl(p.pending)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, bg }) {
  return (
    <div className={`${bg} border-2 border-brand-ink px-3 py-2 text-brand-ink`}>
      <p className="text-[10px] font-bold uppercase leading-tight">{label}</p>
      <p className="font-heading text-lg font-black leading-tight">{value}</p>
    </div>
  );
}
